import { MultiProgress, Progress } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { type IFixedBudgetSubtotal } from '@webapp/hooks/useFormatBudget'
import { BudgetCategoryCardProgressBarStyled } from './styles/BudgetCategoryCardProgressBarStyled'
import messages from '../../messages'
import { WithBudgetProgressBarTooltip } from '../BudgetProgressBarTooltip'

interface IBudgetCategoryCardProgressBarProps {
  isOverBudget: boolean
  committedSpend: IFixedBudgetSubtotal
  pendingRequests: IFixedBudgetSubtotal
  remainingAfterPendingRequests: IFixedBudgetSubtotal
  totalSpend: IFixedBudgetSubtotal
  spendRequest?: IFixedBudgetSubtotal
  largeTooltip?: boolean
}

export const BudgetCategoryCardProgressBar = ({
  isOverBudget,
  committedSpend,
  pendingRequests,
  remainingAfterPendingRequests,
  totalSpend,
  spendRequest,
  largeTooltip = false,
}: IBudgetCategoryCardProgressBarProps) => {
  const intl = useProcurifyIntl()

  const committedPercentage = Math.min(committedSpend.percentage.number, 100)
  const spendRequestPercentage = spendRequest?.percentage.number
    ? Math.min(spendRequest?.percentage.number, 100 - committedPercentage)
    : 0

  const pendingPercentage = Math.min(
    pendingRequests.percentage.number,
    100 - committedPercentage
  )
  const remainingPercentage = Math.max(
    100 - committedPercentage - spendRequestPercentage - pendingPercentage,
    0
  )

  return (
    <BudgetCategoryCardProgressBarStyled>
      <WithBudgetProgressBarTooltip
        isOverBudget={isOverBudget}
        committedSpend={committedSpend}
        pendingRequests={pendingRequests}
        remainingAfterPendingRequests={remainingAfterPendingRequests}
        totalSpend={totalSpend}
        spendRequest={spendRequest}
        large={largeTooltip}
      >
        <figure>
          {isOverBudget ? (
            <MultiProgress>
              <Progress
                value={100}
                label={intl.formatMessage(messages.subtotalsIsOverBudget)}
                color='budgets.overBudget'
                key={intl.formatMessage(messages.subtotalsIsOverBudget)}
              />
            </MultiProgress>
          ) : (
            <MultiProgress>
              <Progress
                value={committedPercentage}
                label={intl.formatMessage(messages.subtotalsCommittedLabel)}
                color='budgets.committed'
                key={intl.formatMessage(messages.subtotalsCommittedLabel)}
              />
              <Progress
                value={spendRequestPercentage}
                label={intl.formatMessage(messages.thisRequestLabel)}
                color='budgets.committed'
                striped
                key={intl.formatMessage(messages.thisRequestLabel)}
              />
              <Progress
                value={pendingPercentage}
                label={intl.formatMessage(messages.subtotalsPendingLabel)}
                color='budgets.pending'
                key={intl.formatMessage(messages.subtotalsPendingLabel)}
              />
              <Progress
                value={remainingPercentage}
                label={intl.formatMessage(messages.subtotalsRemainingLabel)}
                color='budgets.available'
                key={intl.formatMessage(messages.subtotalsRemainingLabel)}
              />
            </MultiProgress>
          )}
        </figure>
      </WithBudgetProgressBarTooltip>
    </BudgetCategoryCardProgressBarStyled>
  )
}
